// Excel-style authoring help for EstimateCostFormulas expressions — the pure
// half of src/components/FormulaExpressionEditor.jsx (kept DOM-free so it's
// unit-testable): which identifier the caret is inside, which documented
// variables match it, and a live check of the whole expression against a
// sample room so a typo or unknown variable is caught before Save.
//
// `vars` is the flat variable list the editor works from — by default the
// static reference; the Formulas tab passes flattenVariableGroups(
// buildFormulaVariableGroups(engineVariableRows)) so Engine Variables
// autocomplete too. `bindings` are extra name -> number values for the
// preview (the live Engine Variable values).
import { FORMULA_VARIABLE_GROUPS } from "../config/formulaVariables";
import { evaluateFormula, functionSpec, resolveName } from "./formulaEngine";
import { roomFormulaVariables } from "./roomFormulaEngine";

export const flattenVariableGroups = (groups) => groups.flatMap(g => g.vars.map(v => ({ ...v, group: g.key, groupTitle: g.title })));
export const ALL_FORMULA_VARIABLES = flattenVariableGroups(FORMULA_VARIABLE_GROUPS);

const IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/g;

// The identifier the caret sits in or immediately after: { word, start, end }
// or null when the caret is on an operator/number/space.
export function identifierAtCaret(text, caret) {
  if (!text) return null;
  let start = caret;
  while (start > 0 && /[A-Za-z0-9_]/.test(text[start - 1])) start--;
  let end = caret;
  while (end < text.length && /[A-Za-z0-9_]/.test(text[end])) end++;
  if (start === end) return null;
  const word = text.slice(start, end);
  if (!/^[A-Za-z_]/.test(word)) return null; // a number, not an identifier
  return { word, start, end };
}

// Variables whose name starts with (then merely contains) the typed prefix,
// case-insensitive; exact match first. Empty prefix lists everything.
export function suggestVariables(prefix, limit = 8, vars = ALL_FORMULA_VARIABLES) {
  const p = (prefix || "").toLowerCase();
  const starts = [], contains = [];
  for (const v of vars) {
    const n = v.name.toLowerCase();
    if (!p || n.startsWith(p)) starts.push(v);
    else if (n.includes(p)) contains.push(v);
  }
  return [...starts, ...contains].slice(0, limit);
}

// Replace the identifier at the caret with `name`; returns { text, caret }.
export function insertVariable(text, caret, name) {
  const id = identifierAtCaret(text, caret);
  const start = id ? id.start : caret, end = id ? id.end : caret;
  const next = text.slice(0, start) + name + text.slice(end);
  return { text: next, caret: start + name.length };
}

// A representative room so an author sees a real number while typing —
// values are only for the preview, never stored.
export const SAMPLE_ROOM = { RoomID: 0, RoomName: "Sample room", LengthFt: 40, WidthFt: 23, HeightFt: 10, ACH: 30, CanvasX: 0, CanvasY: 0, CanvasRot: 0 };
export const SAMPLE_ROOM_LABEL = "a sample 40 × 23 × 10 ft room at 30 ACH";
const SAMPLE_CIRCUIT = { planDistanceFt: 60, routingFactor: 1.25, verticalAllowanceFt: 20, homerunLF: 95, deviceSpacingFt: 12, deviceCount: 6, wasteFactorPct: 0.1 };
const SAMPLE_ASSEMBLY = { routeLF: 100, qty: 1 };

export function sampleBindings(extra = {}) {
  return { ...roomFormulaVariables(SAMPLE_ROOM, {}, { tunables: extra }), ...SAMPLE_CIRCUIT, ...SAMPLE_ASSEMBLY, ...extra };
}

// Check an expression: { ok, value, unknown[], error, groups[], mixed, empty }.
//   unknown — identifiers that aren't documented variables (the most common
//             mistake: a typo or a guessed name)
//   groups  — which binding contexts the referenced variables come from, so
//             the editor can warn when a formula mixes room and circuit
//             variables (never bound together); Engine Variables bind
//             everywhere a room does, so they never count as mixing
//   bound   — (optional) the EXACT name -> number values the formula will see where it is used; when given it replaces the
//             sample room bindings (an assembly component formula sees only qty + Engine Variables, so a room variable
//             like floorArea must read as unknown there, not silently pass)
export function checkExpression(expression, { vars = ALL_FORMULA_VARIABLES, bindings = {}, bound: boundOverride = null } = {}) {
  const text = (expression || "").trim().replace(/^=\s*/, "");
  if (!text) return { ok: false, value: null, unknown: [], unknownFunctions: [], error: null, groups: [], mixed: false, empty: true };
  const byName = new Map(vars.map(v => [v.name, v]));
  const bound = boundOverride || sampleBindings(bindings); // includes every built-in Engine Variable default, so those keys are "known" even without a DB row
  // a name followed by "(" is a function call: supported ones are fine, anything else is reported as an unknown FUNCTION (not a variable)
  const called = [...new Set([...text.matchAll(/([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)].map(m => m[1]))];
  const unknownFunctions = called.filter(n => !functionSpec(n));
  const idents = [...new Set(text.match(IDENT_RE) || [])].filter(n => !called.includes(n));
  const knownNames = new Set([...byName.keys(), ...Object.keys(bound)]);
  const unknown = idents.filter(n => resolveName(n, knownNames) == null);
  // (a name that only differs by case resolves and evaluates — nothing to flag)
  const varNames = new Set(byName.keys());
  const groups = [...new Set(idents.map(n => resolveName(n, varNames)).filter(Boolean).map(k => byName.get(k).group))];
  let value = null, error = null;
  try { value = evaluateFormula(text, bound); }
  catch (e) { error = String(e.message || e).replace(/ in formula:.*$/, ""); }
  const has = k => groups.includes(k);
  const mixed = !boundOverride && ((has("circuit") && (has("room") || has("engine") || has("assembly")))
    || (has("assembly") && (has("room") || has("engine"))));
  return { ok: !unknown.length && !unknownFunctions.length && error == null && Number.isFinite(value), value, unknown, unknownFunctions, error, groups, mixed, empty: false };
}
